import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function CentralBedfordshireCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Central Bedfordshire Pothole Claims | 17,446 Repairs in Five Years | Fixtyer"
        description="Central Bedfordshire Council earns GREEN spend scorecards yet B and C roads in RED condition rose to 3.4% as green roads fell to 76%. The council admits pothole counts are estimated. See what the DfT AMBER rating means for your claim."
        canonical="/councils/central-bedfordshire"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Central Bedfordshire', url: '/councils/central-bedfordshire' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Central Bedfordshire has a GREEN spend rating — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but the rating that matters for your claim is road condition — and Central Bedfordshire is AMBER overall because B and C roads in RED condition rose from 2.7% to 3.4% between 2022 and 2024 while green B/C roads fell from 81.8% to 76%. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on how much the council spends in aggregate.',
          },
          {
            question: 'The council says pothole numbers are estimates — does that weaken my claim?',
            answer: 'No — it strengthens it. Central Bedfordshire admits it does not have a works-order category called pothole, doubles work-order counts to produce its published figures, and will not have proper pothole tracking until April 2026. That is an admission its reactive repair records are incomplete — which makes prior reports, photos and FixMyStreet history even more important for proving actual notice of your specific defect.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 815km — 57% of Central Bedfordshire\'s 1,425km network. The council publishes RED-condition percentages only for U-roads, not amber or green breakdowns. At the last survey, 16% of U-roads were in RED condition — roughly 130km of residential streets and rural lanes. U-road condition is monitored via mobile Vaisala AI surveys across a rolling one-year cycle rather than annual SCANNER.',
          },
          {
            question: 'Does missing 2020/21 condition data affect my claim?',
            answer: 'It can. Central Bedfordshire\'s own report states that 2021 A-road and B/C road condition data is missing because resources were diverted to the COVID emergency. Category 3b roads are surveyed only every two years. If your incident falls in a gap year or on a road class with infrequent surveys, the council\'s knowledge of that road\'s condition at the time of your damage is harder to establish — which cuts both ways on Section 58.',
          },
          {
            question: 'Only 3 miles of resurfacing planned for 2025/26 — does that mean the roads are fixed?',
            answer: 'No. Despite GREEN spend and 72% preventative maintenance, Central Bedfordshire plans just 3 miles of carriageway resurfacing in 2025/26 alongside 6 miles of surface dressing and 2 miles of micro asphalt. B/C roads in amber or worse rose to 24.1% in 2024. The council still expects to address an estimated 3,488 potholes through planned and reactive works — and filled an estimated 3,194 in 2024/25 alone.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Central Bedfordshire Council"
        councilSlug="central-bedfordshire"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/central-bedfordshire"
        headline="Central Bedfordshire Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Central Bedfordshire</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Central Bedfordshire: GREEN Spend, Slipping B and C Roads
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Central Bedfordshire spends well above its DfT capital allocation and earns a{' '}
            <strong className="text-emerald-400">GREEN</strong> spend scorecard. Yet the overall rating is{' '}
            <strong className="text-amber-400">AMBER</strong> — because B and C roads in RED condition rose to{' '}
            <strong className="text-rose-400">3.4%</strong> in 2024 as green roads fell to 76%, and the council's own
            estimated pothole count reached <strong className="text-rose-400">17,446 repairs in five years</strong> on
            a network where it admits it does not properly record potholes at all.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">76%</div>
            <div className="text-white font-medium mt-2">B/C roads in good condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Down from 81.8% in 2022. RED-condition B/C roads rose from 2.7% to 3.4% in the same period —
              while the council plans just 3 miles of resurfacing for 2025/26.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER survey data from Central Bedfordshire's own transparency report — A-roads
            stable but slipping, B/C roads clearly declining, and incomplete data flagged by the DfT
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (168km — 11.8% of network): slipping slowly</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">1.2%</td>
                    <td className="px-4 py-2 text-center">16.6%</td>
                    <td className="px-4 py-2 text-center">82.2%</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No data collected</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">1.4%</td>
                    <td className="px-4 py-2 text-center">13.7%</td>
                    <td className="px-4 py-2 text-center">84.9%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">1.6%</td>
                    <td className="px-4 py-2 text-center">14.6%</td>
                    <td className="px-4 py-2 text-center">83.7%</td>
                  </tr>
                  <tr className="bg-brand-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">2.2%</td>
                    <td className="px-4 py-2 text-center font-bold">16.6%</td>
                    <td className="px-4 py-2 text-center font-bold">81.2%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              The council notes A-roads in the green category remain above 80% and above the national average.
              But RED A-roads have nearly doubled since 2020 (1.2% → 2.2%), and green roads have fallen 1
              percentage point in two years. National Highways maintains 120km of the 168km A-road total.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (442km — 31% of network): declining</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No data collected</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center text-surface-400 italic" colSpan={3}>No data collected</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2.7%</td>
                    <td className="px-4 py-2 text-center">15.5%</td>
                    <td className="px-4 py-2 text-center">81.8%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">2.8%</td>
                    <td className="px-4 py-2 text-center">17.8%</td>
                    <td className="px-4 py-2 text-center">79.4%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">3.4%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">20.7%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">76.0%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              The council's own summary: main roads have "deteriorated slightly over the last 3 years." In
              numbers: RED B/C roads up a quarter since 2022 (2.7% → 3.4%), amber up five points (15.5% → 20.7%),
              and good-condition roads down nearly six points (81.8% → 76%). A quarter of the B/C network now
              needs — or will soon need — maintenance.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Well-Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£11.1m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£14.3m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">72%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Central Bedfordshire has exceeded its DfT allocation every year in the transparency table — and
              plans <span className="text-emerald-400">29% more capital spend</span> than allocated in 2025/26.
              Yet B/C road condition is still slipping. The DfT flags this council's ratings with incomplete
              condition data (***), and only 3 miles of full resurfacing are planned for the year ahead.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 815km U-Road Picture
          </h2>
          <p className="text-surface-600 mb-8">
            57% of the network is unclassified roads — monitored by mobile AI surveys, not annual SCANNER
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12.4%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">12.4%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">18.0%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">16.0%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">16.0%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The +2% Claim</h3>
              <p className="text-surface-600 text-sm mb-3">
                The council states U-road condition "has improved by +2% over the last 3 years" — from 18%
                RED in 2022 to 16% in 2024. That is modest recovery from a spike, not a return to the 12.4%
                baseline of 2020/21.
              </p>
              <p className="text-surface-600 text-sm">
                At 16% RED, roughly <strong>130km of U-roads</strong> may need maintenance treatment — estate
                roads, village lanes and rural access routes across Dunstable, Leighton Buzzard, Biggleswade
                and the surrounding parishes.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">No Amber/Green Breakdown</h3>
              <p className="text-amber-800 text-sm mb-3">
                Unlike A and B/C roads, the transparency report publishes only RED percentages for U-roads.
                You cannot see what share is amber versus green — a significant blind spot on 57% of the network.
              </p>
              <p className="text-amber-800 text-sm">
                U-road condition uses Vaisala AI Mobile Condition surveys deployed in inspectors' vehicles,
                with data collected across one year for the whole network then refreshed — a different methodology
                to SCANNER on classified roads.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing
              the condition of its roads. For Central Bedfordshire's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — and was it in the 2022 spike year or after?</li>
              <li>• With only RED percentages published, how does the council demonstrate knowledge of amber roads before they fail?</li>
              <li>• Category 3b roads are SCANNER-surveyed only every two years — was yours in a survey year?</li>
              <li>• Does the mobile AI survey cycle mean your road went unmonitored for months at a time?</li>
            </ul>
            <p className="text-white font-medium">
              A council that cannot even categorise its pothole repairs cannot easily claim comprehensive
              knowledge of every defect on every residential road.
            </p>
          </div>
        </div>
      </section>

      {/* The pothole estimate admission */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            17,446 Estimated Repairs — And The Counting Caveat
          </h2>
          <p className="text-surface-600 mb-8">
            The council does not track potholes — then doubles work-order counts to produce published figures
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Central Bedfordshire Council do not currently have a works ordering category called 'pothole'. Fixing potholes is incorporated into most schemes and the number of potholes addressed are not specifically recorded."
              source="Central Bedfordshire Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="As almost all orders that mentioned potholes included at least two potholes, the number of orders has been doubled to give an estimate of potholes filled."
              source="Central Bedfordshire Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Estimated potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">3,952</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">4,170</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">2,624</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">3,506</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold">3,194</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17,446</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">11% of Spend on Reactive Potholes</h3>
              <p className="text-surface-600 text-sm">
                In 2024/25, approximately 11% of highways spend went on revenue works orders for reactive
                pothole repairs, with a further 6% on preventative pothole works orders. The council expects
                to address 3,488 potholes in 2025/26 through planned and reactive works — still an estimate.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">No Proper Tracking Until April 2026</h3>
              <p className="text-amber-800 text-sm">
                A mechanism to record potholes addressed will be included in the new asset management system
                going live on 1 April 2026. Until then, every published pothole figure is a derived estimate —
                not a counted repair log. That matters when the council cites its repair volumes in defence
                of a specific claim.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Survey gaps and admissions */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Survey Gaps And The Delay Admission
          </h2>
          <p className="text-surface-600 mb-8">
            Missing COVID-year data, biennial 3b surveys, and the council's own prioritisation logic
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The overall condition of residential roads (Unclassified (U) Roads) in Central Bedfordshire has improved by +2% over the last 3 years. The condition of the main roads (A, B & C Roads) has deteriorated slightly over the last 3 years."
              source="Central Bedfordshire Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="In 2021 there is missing data as resource was diverted to deal with the national covid emergency."
              source="Central Bedfordshire Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Road category</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Survey method</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Frequency</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3">Category 2 roads</td>
                  <td className="px-4 py-3 text-center">SCANNER</td>
                  <td className="px-4 py-3 text-center">Annually</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3">Category 3a roads</td>
                  <td className="px-4 py-3 text-center">SCANNER</td>
                  <td className="px-4 py-3 text-center">Annually</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3">Category 3b roads</td>
                  <td className="px-4 py-3 text-center">SCANNER</td>
                  <td className="px-4 py-3 text-center font-bold text-amber-600">Every 2 years</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">U roads (4a/4b)</td>
                  <td className="px-4 py-3 text-center">Vaisala AI mobile</td>
                  <td className="px-4 py-3 text-center">Rolling 1-year cycle</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">DfT Incomplete Data Flag</h3>
              <p className="text-surface-600 text-sm">
                The DfT marks Central Bedfordshire with *** in its ratings table: "The overall rating and
                best practice and condition scorecards for this local highway authority are based on incomplete
                road condition data." Missing 2020/21 A and B/C figures contributed to that flag.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">3 Miles of Resurfacing in 2025/26</h3>
              <p className="text-amber-800 text-sm">
                Despite £14.3m projected capital spend, the council plans just 3 miles of carriageway
                resurfacing, 6 miles of surface dressing and 2 miles of micro asphalt in 2025/26 — 12 miles
                of surface improvement total across 1,425km. The DfT carriageway treatment data shows only
                5km projected for resurfacing and 13km for preventative treatments nationally reported.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Central Bedfordshire invests heavily — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend exceeds DfT allocation every year in the report
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 72–82% preventative maintenance share across five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road green condition still above 80% and above national average
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Data-led asset management, three-year capital programme, innovation trials
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £5m extra surface dressing in 2023/24 across 55 miles — council cites fewer winter potholes
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on well-surveyed classified roads. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — B/C RED up from 2.7% to 3.4%, green down to 76%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> DfT *** flag for incomplete condition data including missing 2021 surveys
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 57% of network is U-roads with RED-only reporting — 16% in worst condition
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Pothole figures are estimates — council doubles work orders and lacks a pothole category
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Category 3b roads surveyed only every two years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Only 3 miles of full resurfacing planned despite deteriorating B/C data
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and a data-led capital programme, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the RED-only reporting gap and AI survey cycle are structural arguments</li>
              <li>• Whether your road was in a SCANNER survey year — 3b roads are only measured biennially</li>
              <li>• Challenge estimated pothole totals — the council's own doubling methodology undermines aggregate repair claims</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Central Bedfordshire's own transparency data — including admissions about incomplete surveys
              and estimated pothole counts — where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Central Bedfordshire?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No pothole-estimate challenge</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ B/C road decline documented</li>
                <li>✅ Incomplete survey data argued</li>
                <li>✅ 17,446 estimated repairs cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Central Bedfordshire</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=central-bedfordshire"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Central Bedfordshire Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Central Bedfordshire has a GREEN spend rating — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but the rating that matters for your claim is road
                condition — and Central Bedfordshire is AMBER overall because B and C roads in RED condition
                rose from 2.7% to 3.4% between 2022 and 2024 while green B/C roads fell from 81.8% to 76%.
                Section 58 turns on whether the specific defect that damaged your vehicle was reasonably
                inspected and repaired, not on how much the council spends in aggregate.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                The council says pothole numbers are estimates — does that weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No — it strengthens it. Central Bedfordshire admits it does not have a works-order category
                called pothole, doubles work-order counts to produce its published figures, and will not have
                proper pothole tracking until April 2026. That is an admission its reactive repair records are
                incomplete — which makes prior reports, photos and FixMyStreet history even more important for
                proving actual notice of your specific defect.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 815km — 57% of Central Bedfordshire's 1,425km network. The council publishes
                RED-condition percentages only for U-roads, not amber or green breakdowns. At the last survey,
                16% of U-roads were in RED condition — roughly 130km of residential streets and rural lanes.
                U-road condition is monitored via mobile Vaisala AI surveys across a rolling one-year cycle
                rather than annual SCANNER.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does missing 2020/21 condition data affect my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can. Central Bedfordshire's own report states that 2021 A-road and B/C road condition data
                is missing because resources were diverted to the COVID emergency. Category 3b roads are
                surveyed only every two years. If your incident falls in a gap year or on a road class with
                infrequent surveys, the council's knowledge of that road's condition at the time of your damage
                is harder to establish — which cuts both ways on Section 58.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Only 3 miles of resurfacing planned for 2025/26 — does that mean the roads are fixed?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Despite GREEN spend and 72% preventative maintenance, Central Bedfordshire plans just 3 miles
                of carriageway resurfacing in 2025/26 alongside 6 miles of surface dressing and 2 miles of micro
                asphalt. B/C roads in amber or worse rose to 24.1% in 2024. The council still expects to address
                an estimated 3,488 potholes through planned and reactive works — and filled an estimated 3,194
                in 2024/25 alone.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.centralbedfordshire.gov.uk/info/55/transport_roads_and_parking/592/roads_strategies"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Central Bedfordshire Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
